import { AncestorIdData, AncestorNameData, FolderId } from 'modules/domains/foldering';
import styled from 'styled-components';
import { useSearchParam } from 'react-use';
import { colors, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';

import { SimpleBreadcrumb } from './SimpleBreadcrumb';
import { FolderBreadcrumbPopover } from './FolderBreadcrumbPopover';

const StyledContainer = styled.div`
  align-items: center;
  background: rgba(230, 251, 255, 0.4);
  border-top: ${spacing.single} solid ${colors.grey15};
  display: flex;
  height: ${pxToRem(50)};
  justify-content: flex-start;
  padding: 0 ${spacing.double};
`;

type FolderBreadcrumbsProps = {
  /** folder object with name and ancestors */
  folderAncestors: (AncestorIdData & AncestorNameData)[];
  rootFolderId: FolderId;
};

export const FolderBreadcrumbs = ({
  folderAncestors = [],
  rootFolderId,
  ...props
}: FolderBreadcrumbsProps) => {
  const currentFolder = useSearchParam('folderId') || rootFolderId.toString();
  const activeFolderId = currentFolder ? parseInt(currentFolder, 10) : rootFolderId;
  const isCurrentFolder = (id: number | undefined) => (id ? id === activeFolderId : false);

  const ultimateFolder = folderAncestors[folderAncestors.length - 1];
  const penultimateFolder = folderAncestors[folderAncestors.length - 2];
  const rootFolder = folderAncestors[0];
  if (folderAncestors.length > 4 && ultimateFolder && penultimateFolder && rootFolder) {
    return (
      <StyledContainer data-test="folder-breadcrumb-bar" {...props}>
        <SimpleBreadcrumb
          data={rootFolder}
          hideIcon={true}
          isCurrentFolder={isCurrentFolder(rootFolder?.id)}
          isRoot
          nameOverride="All"
          nestIndex={0}
        />
        <FolderBreadcrumbPopover
          contents={folderAncestors.slice(1, folderAncestors.length - 2)}
          index={1}
          id={-2}
        />
        <SimpleBreadcrumb
          data={penultimateFolder}
          isCurrentFolder={isCurrentFolder(penultimateFolder?.id)}
          nestIndex={folderAncestors.length - 2}
        />
        <SimpleBreadcrumb
          data={ultimateFolder}
          isCurrentFolder={isCurrentFolder(ultimateFolder?.id)}
          isLastCrumb
          nestIndex={folderAncestors.length - 1}
        />
      </StyledContainer>
    );
  }

  return (
    <StyledContainer data-test="folder-breadcrumb-bar" {...props}>
      {folderAncestors.map((folder, idx, arr) => (
        <SimpleBreadcrumb
          data={folder}
          key={idx}
          hideIcon={idx === 0}
          isCurrentFolder={isCurrentFolder(folder.id)}
          isLastCrumb={idx === arr.length - 1}
          isRoot={idx === 0}
          nameOverride={folder.id === rootFolderId ? 'All' : ''}
          nestIndex={idx}
        />
      ))}
    </StyledContainer>
  );
};
